import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { BookingService } from '../../services/booking.service';
import { AssetService } from '../../services/asset.service';
import { SpacePublicRouteService } from '../../services/space-public-route.service';
import {
  Booking, BOOKING_STATUS_META,
  compareBookingsByEventAsc,
  guestNeedsSecurityDepositAction,
} from '../../models/booking.model';
import { onlineFeeEurosFromBooking } from '../../shared/booking/booking-amounts.utils';
import {
  formalizationWorkflowHint,
  resolveFormalizationDeadlineMode,
  resolveFormalizationWindowDays,
} from '../../shared/booking/formalization-deadline.utils';
import { Timestamp } from '@angular/fire/firestore';
import { ReviewService } from '../../services/review.service';
import { REVIEW_MAX_TEXT_LENGTH, REVIEW_MIN_TEXT_LENGTH } from '../../models/review.model';
import { GuestSecurityDepositPanelComponent } from '../guest-security-deposit-panel/guest-security-deposit-panel.component';
import { MyBookingsWithdrawalComponent } from '../my-bookings-withdrawal/my-bookings-withdrawal.component';
import { DEFAULT_CONDITIONS } from '../../models/asset.model';

@Component({
  selector: 'app-my-bookings',
  imports: [LucideAngularModule, DecimalPipe, GuestSecurityDepositPanelComponent, MyBookingsWithdrawalComponent],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './my-bookings.component.html',
})
export class MyBookingsComponent implements OnInit, OnDestroy {
  private bookingService = inject(BookingService);
  private assetService   = inject(AssetService);
  private reviewService  = inject(ReviewService);
  private spaceRoutes    = inject(SpacePublicRouteService);
  private router         = inject(Router);
  private route          = inject(ActivatedRoute);

  readonly bookings = signal<Booking[]>([]);

  readonly activeBookings = computed(() =>
    this.bookings()
      .filter(b => b.status !== 'COMPLETED' && b.status !== 'CANCELLED')
      .sort(compareBookingsByEventAsc),
  );

  readonly pastBookings = computed(() =>
    this.bookings()
      .filter(b => b.status === 'COMPLETED' || b.status === 'CANCELLED')
      .sort((a, b) => -compareBookingsByEventAsc(a, b)),
  );
  readonly spaceNames = signal<Record<string, string>>({});
  readonly spaceCancellationDays = signal<Record<string, number>>({});
  readonly loading    = signal(true);

  readonly statusMeta = BOOKING_STATUS_META;

  readonly cancelTarget = signal<Booking | null>(null);
  readonly cancelAcknowledged = signal(false);
  readonly cancelSaving = signal(false);
  readonly cancelError = signal<string | null>(null);
  readonly depositSyncMessage = signal<string | null>(null);

  readonly reviewTarget = signal<Booking | null>(null);
  readonly reviewRating = signal(0);
  readonly reviewText = signal('');
  readonly reviewSaving = signal(false);
  readonly reviewError = signal<string | null>(null);
  readonly reviewedBookingIds = signal<Set<string>>(new Set());

  readonly reviewMinLength = REVIEW_MIN_TEXT_LENGTH;
  readonly reviewMaxLength = REVIEW_MAX_TEXT_LENGTH;

  readonly guestNeedsSecurityDepositAction = guestNeedsSecurityDepositAction;

  readonly bookingsTab = signal<'upcoming' | 'history'>('upcoming');
  setBookingsTab(tab: 'upcoming' | 'history'): void { this.bookingsTab.set(tab); }

  private sub?: Subscription;

  ngOnInit(): void {
    void this.#syncDepositReturnIfNeeded();
    this.sub = this.bookingService.getUserBookings().subscribe(async all => {
      this.bookings.set(all);
      this.loading.set(false);
      await this.#refreshReviewFlags(all);

      const reviewId = this.route.snapshot.queryParamMap.get('review')?.trim();
      if (reviewId) {
        const booking = all.find(b => b.id === reviewId);
        if (booking?.status === 'COMPLETED' && !this.reviewedBookingIds().has(reviewId)) {
          this.openReviewModal(booking);
        }
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { review: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }

      const ids = [...new Set(all.map(b => b.spaceId))];
      const entries = await Promise.all(
        ids.map(async id => {
          const space = await this.assetService.getSpaceById(id);
          return [
            id,
            {
              name: space?.name ?? 'Espacio desconocido',
              cancellationDays: space?.conditions?.cancellationDays ?? DEFAULT_CONDITIONS.cancellationDays,
            },
          ] as [string, { name: string; cancellationDays: number }];
        })
      );
      this.spaceNames.set(Object.fromEntries(entries.map(([id, v]) => [id, v.name])));
      this.spaceCancellationDays.set(Object.fromEntries(entries.map(([id, v]) => [id, v.cancellationDays])));
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async #syncDepositReturnIfNeeded(): Promise<void> {
    const sessionId = this.route.snapshot.queryParamMap.get('deposit_session_id')?.trim();
    if (!sessionId) return;
    const bookingId = this.route.snapshot.queryParamMap.get('deposit_booking_id')?.trim();
    try {
      await this.bookingService.syncDepositFromCheckoutSession(sessionId, bookingId);
      this.depositSyncMessage.set('Retención de fianza registrada correctamente.');
    } catch {
      this.depositSyncMessage.set('No se pudo confirmar la retención. Contacta con soporte si el cargo aparece en tu banco.');
    } finally {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { deposit_session_id: null, deposit_cancelled: null, deposit_booking_id: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  formatDate(isoDate: string): string {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  }

  async #refreshReviewFlags(bookings: Booking[]): Promise<void> {
    const completed = bookings.filter(b => b.status === 'COMPLETED' && b.id);
    const reviewed = new Set<string>();
    await Promise.all(
      completed.map(async b => {
        const review = await this.reviewService.getReviewForBooking(b.id!);
        if (review) reviewed.add(b.id!);
      }),
    );
    this.reviewedBookingIds.set(reviewed);
  }

  canReviewBooking(booking: Booking): boolean {
    return booking.status === 'COMPLETED'
      && !!booking.id
      && !this.reviewedBookingIds().has(booking.id);
  }

  openReviewModal(booking: Booking, event?: Event): void {
    event?.stopPropagation();
    if (!this.canReviewBooking(booking)) return;
    this.reviewTarget.set(booking);
    this.reviewRating.set(0);
    this.reviewText.set('');
    this.reviewError.set(null);
  }

  closeReviewModal(): void {
    if (this.reviewSaving()) return;
    this.#resetReviewModal();
  }

  #resetReviewModal(): void {
    this.reviewTarget.set(null);
    this.reviewRating.set(0);
    this.reviewText.set('');
    this.reviewError.set(null);
  }

  setReviewRating(value: number): void {
    this.reviewRating.set(value);
  }

  onReviewTextInput(event: Event): void {
    this.reviewText.set((event.target as HTMLTextAreaElement).value);
  }

  reviewTextLength(): number {
    return this.reviewText().trim().length;
  }

  canSubmitReview(): boolean {
    return this.reviewRating() >= 1
      && this.reviewRating() <= 5
      && this.reviewTextLength() >= REVIEW_MIN_TEXT_LENGTH
      && !this.reviewSaving();
  }

  async submitReview(): Promise<void> {
    if (!this.canSubmitReview()) return;
    const booking = this.reviewTarget();
    if (!booking) return;

    this.reviewSaving.set(true);
    this.reviewError.set(null);
    try {
      await this.reviewService.createReview(booking, this.reviewRating(), this.reviewText());
      const id = booking.id!;
      this.reviewedBookingIds.update(set => new Set([...set, id]));
      this.#resetReviewModal();
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message
        ?? 'No se pudo guardar la valoración. Inténtalo de nuevo.';
      this.reviewError.set(msg);
    } finally {
      this.reviewSaving.set(false);
    }
  }

  goExplore(): void {
    this.router.navigate(['/explorar']);
  }

  workflowText(booking: Booking): string {
    switch (booking.status) {
      case 'PENDING_FORMALIZATION': {
        const paymentMs = booking.paymentTimestamp instanceof Timestamp ?
          booking.paymentTimestamp.toMillis() :
          undefined;
        const days = resolveFormalizationWindowDays(
          booking.date,
          booking.formalizationWindowDays,
          paymentMs,
        );
        const mode = resolveFormalizationDeadlineMode(
          booking.formalizationDeadlineMode,
          days,
        );
        return formalizationWorkflowHint(days, mode);
      }
      case 'PENDING_VERIFICATION':
        return 'Justificante enviado. Esperando validación final del propietario.';
      case 'CONFIRMED':
        if (guestNeedsSecurityDepositAction(booking)) {
          return 'Abona la fianza reembolsable (48 h antes del evento).';
        }
        if (booking.securityDepositStatus === 'submitted') {
          return 'Fianza enviada. Pendiente de confirmación del propietario.';
        }
        return 'Reserva firme y legalmente cerrada.';
      case 'ACTIVE':
        return 'Evento en curso.';
      case 'COMPLETED':
        return 'Evento finalizado.';
      case 'CANCELLED':
        return 'Reserva cancelada.';
      default:
        return '';
    }
  }

  primaryActionLabel(booking: Booking): string {
    switch (booking.status) {
      case 'PENDING_FORMALIZATION':
      case 'PENDING_VERIFICATION':
        return 'Completar formalización';
      case 'CONFIRMED':
        return guestNeedsSecurityDepositAction(booking) ? 'Gestionar fianza' : 'Ver espacio';
      case 'ACTIVE':
      case 'COMPLETED':
        return 'Ver espacio';
      case 'CANCELLED':
      default:
        return 'Explorar';
    }
  }

  canCancelFormalization(booking: Booking): boolean {
    return booking.status === 'PENDING_FORMALIZATION' && !!booking.id;
  }

  platformFeeAmount(booking: Booking): number {
    return onlineFeeEurosFromBooking(booking);
  }

  openCancelModal(booking: Booking, event?: Event): void {
    event?.stopPropagation();
    if (!this.canCancelFormalization(booking)) return;
    this.cancelTarget.set(booking);
    this.cancelAcknowledged.set(false);
    this.cancelError.set(null);
  }

  closeCancelModal(): void {
    if (this.cancelSaving()) return;
    this.cancelTarget.set(null);
    this.cancelAcknowledged.set(false);
    this.cancelError.set(null);
  }

  async confirmCancelBooking(): Promise<void> {
    if (this.cancelSaving() || !this.cancelAcknowledged()) return;
    const booking = this.cancelTarget();
    if (!booking?.id) return;

    this.cancelSaving.set(true);
    this.cancelError.set(null);
    try {
      await this.bookingService.cancelFormalizationBooking(booking.id);
      this.cancelTarget.set(null);
      this.cancelAcknowledged.set(false);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message
        ?? 'No se pudo cancelar la reserva. Inténtalo de nuevo.';
      this.cancelError.set(msg);
    } finally {
      this.cancelSaving.set(false);
    }
  }

  goPrimaryAction(booking: Booking): void {
    switch (booking.status) {
      case 'PENDING_FORMALIZATION':
      case 'PENDING_VERIFICATION':
        this.router.navigate(['/reserva', booking.id, 'formalizar']);
        return;
      case 'CONFIRMED':
        if (guestNeedsSecurityDepositAction(booking)) return;
        void this.spaceRoutes.navigateToSpaceById(booking.spaceId);
        return;
      case 'ACTIVE':
      case 'COMPLETED':
        void this.spaceRoutes.navigateToSpaceById(booking.spaceId);
        return;
      case 'CANCELLED':
      default:
        this.goExplore();
    }
  }
}
